import { AsyncAction, FluxStandardAction, ActionType as PromiseActionType } from 'redux-promise-middleware'

import { BackendError, ErrorState, ErrorType, ReduxStoreState } from 'Models'
import {
  FETCH_MENU_PLAN_REJECTED,
  FETCH_USER_GOAL_NUTRITIONS_REJECTED,
  HIDE_ACCESS_DENIED_MODAL,
  REMOVE_ERROR,
  REMOVE_ERROR_MESSAGES,
  getOriginalType,
  isFailure,
  isSuccess
} from 'ReduxStore/actionTypes'
import { createDeepEqualitySelector } from 'ReduxStore/common'
import { EbalanceBackendService } from 'Services'
import { getErrorMessagesFromBackendResponse } from 'Utils/helpers/errorMessages'

// Actions

export const addErrorAction = (error: Error): FluxStandardAction => ({
  type: `SOMETHING_CUSTOM_${PromiseActionType.Rejected}`,
  payload: error
})

export const removeErrorAction = (type: string): AsyncAction => ({
  type: REMOVE_ERROR,
  meta: { type: getOriginalType(type) }
})

export const removeErrorMessagesAction = (): AsyncAction => ({
  type: REMOVE_ERROR_MESSAGES
})

export const hideAccessDeniedModalAction = (): AsyncAction => ({
  type: HIDE_ACCESS_DENIED_MODAL
})

// Reducer and state
export const initialState: ErrorState = {
  messages: [],
  showAccessDeniedModal: false,
  list: {}
}

const ErrorReducer = (state = initialState, { type, payload, meta = {} }: FluxStandardAction): ErrorState => {
  const originalType = getOriginalType(type)
  const response = payload?.response || null
  // runs on any access permission (HTTP 403)
  if (response && response.status === 403) {
    return {
      ...state,
      showAccessDeniedModal: true
    }
  }

  // runs on any REJECTED action type
  if (isFailure(type)) {
    if (meta.skipLoadingIndicator) {
      return state
    }
    const error = payload as BackendError
    let messages =
      error.userErrorMessages || // first check for custom error messages provided by the backend services class
      EbalanceBackendService.getBackendErrorMessages(error) || // then check for common error messages that are services independent
      getErrorMessagesFromBackendResponse(response) // finally check default / fallback error handling

    /*
    The menuplan endpoint (and goal nutritions endpoint too) throws an unintentional 409 error once a day.
    These errors do not have any effect on the frontend except displaying confusing client error toast messages.
    As a workaround, the toast messages get suppressed.
    */

    if (type === FETCH_MENU_PLAN_REJECTED && response.status === 409) {
      messages = []
    }

    if (type === FETCH_USER_GOAL_NUTRITIONS_REJECTED && response.status === 409) {
      messages = []
    }

    return {
      ...state,
      messages,
      list: {
        ...state.list,
        [originalType]: {
          reduxType: type,
          response
        }
      }
    }
  }

  // runs on any FULFILLED action type
  // clears all errors related to the original action type
  if (isSuccess(type)) {
    const newList = { ...state.list }

    delete newList[originalType]

    return {
      ...state,
      ...(state.list[originalType] ? { list: newList } : {})
    }
  }

  switch (type) {
    case REMOVE_ERROR: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [meta.type]: _, ...list } = state.list
      return {
        ...state,
        messages: [],
        list
      }
    }
    case REMOVE_ERROR_MESSAGES:
      return {
        ...state,
        messages: []
      }
    case HIDE_ACCESS_DENIED_MODAL:
      return {
        ...state,
        showAccessDeniedModal: false
      }
    default:
      return state
  }
}

// Selector
export const errorSelector = ({ error }: ReduxStoreState): ErrorState => error

export const errorListSelector = createDeepEqualitySelector([errorSelector], ({ list }) => Object.values(list))

export const errorMessagesSelector = createDeepEqualitySelector([errorSelector], ({ messages }) => {
  return messages
})

export const showAccessDeniedModalSelector = createDeepEqualitySelector(
  [errorSelector],
  ({ showAccessDeniedModal }) => showAccessDeniedModal
)

export const actionTypeErrorSelector = ({ error }: ReduxStoreState, actionType: string): ErrorType =>
  error.list[actionType]

export const hasActionFailedSelector = ({ error }: ReduxStoreState, actionType: string): boolean =>
  Object.keys(error.list).some((type) => type.indexOf(actionType) !== -1)

export default ErrorReducer
